/**
 * AbsDesk display helpers — provenance strip + commodity-honest price labels.
 * Pure functions over ABS run-output shapes (meta / summary / priced / decks / sensitivity).
 */

import { isOilPrimary } from '../utils/commodityDisplay.js';

/** Resolve oil vs gas from ABS output (priced assumptions → summary → meta). */
export function resolveAbsPrimaryProduct(output) {
  const priced = output?.priced?.assumptions || {};
  const summary = output?.summary || {};
  const meta = output?.meta || {};
  return (
    priced.primaryProduct ||
    priced.product ||
    summary.primaryProduct ||
    summary.product ||
    meta.primaryProduct ||
    meta.product ||
    null
  );
}

export function absIsOil(output) {
  return isOilPrimary({ primaryProduct: resolveAbsPrimaryProduct(output) });
}

/**
 * Column / series label for sensitivity + agency decks.
 * Oil → WTI (never HH). Gas → HH. Unknown → Price.
 */
export function absPriceSeriesLabel(outputOrPrimary) {
  if (typeof outputOrPrimary === 'string' || outputOrPrimary == null) {
    if (outputOrPrimary === 'oil') return 'WTI';
    if (outputOrPrimary === 'gas') return 'HH';
    return 'Price';
  }
  const primary = resolveAbsPrimaryProduct(outputOrPrimary);
  if (primary === 'oil') return 'WTI';
  if (primary === 'gas') return 'HH';
  if (absIsOil(outputOrPrimary)) return 'WTI';
  return primary ? 'HH' : 'Price';
}

/** Pick the price number from a deck/sensitivity row for the active commodity. */
export function absRowPrice(row, oil) {
  if (!row) return null;
  if (oil) {
    const wti = row.wti != null ? Number(row.wti) : null;
    if (wti != null && !Number.isNaN(wti)) return wti;
    // Engine still mirrors oil into hh — use as numeric fallback only; label stays WTI.
    const hh = row.hh != null ? Number(row.hh) : null;
    return hh != null && !Number.isNaN(hh) ? hh : null;
  }
  const hh = row.hh != null ? Number(row.hh) : null;
  return hh != null && !Number.isNaN(hh) ? hh : null;
}

/** ARO treatment from solve / summary / underwritingStatus (unknown | treatedAsZero | funded). */
export function resolveAroTreatment(output) {
  const uw = output?.underwritingStatus || output?.solve?.underwritingStatus || {};
  const solve = output?.solve || {};
  const headline = output?.summary?.headline || {};
  return (
    uw.aroTreatment ||
    solve.aroTreatment ||
    headline.aroTreatment ||
    null
  );
}

export function absAroIsUnknown(output) {
  const uw = output?.underwritingStatus || output?.solve?.underwritingStatus || {};
  const solve = output?.solve || {};
  if (solve.aroUnknown === true) return true;
  if (uw.status === 'blocked' && Array.isArray(uw.blockers) && uw.blockers.includes('aro_unknown')) {
    return true;
  }
  return resolveAroTreatment(output) === 'unknown';
}

/** Short banner / label copy for ARO treatment visibility on AbsDesk. */
export function absAroTreatmentLabel(treatment) {
  switch (treatment) {
    case 'funded':
      return 'ARO funded — reserve deducted from collateral PV';
    case 'treatedAsZero':
      return 'ARO treated as $0 — explicit underwriting acknowledgment';
    case 'unknown':
      return 'ARO unknown — optimistic collateral blocked until funded or treated-as-zero';
    default:
      return 'ARO treatment not reported';
  }
}

/** Deck provenance label for AbsDesk (221c). */
export function absDeckProvenanceLabel(output) {
  const deck = output?.meta?.deck || output?.priced?.deck || {};
  const prov = deck.provenance || {};
  const parts = [];
  if (deck.deckId) parts.push(`deck ${deck.deckId}`);
  if (deck.source) parts.push(String(deck.source));
  if (prov.class) parts.push(String(prov.class));
  if (prov.authority) parts.push(String(prov.authority));
  if (deck.deckExtendedFrom) parts.push(`extended from ${deck.deckExtendedFrom}`);
  return parts.length ? parts.join(' · ') : 'Price deck not reported';
}

/**
 * Priced-lines sample rows for Coverage table.
 * Includes unattributed column when any sample row has non-zero unattributed.
 */
export function absPricedLinesTableModel(output, { limit = 24 } = {}) {
  const block = output?.pricedLines || {};
  const cols = block.columns || [];
  const sample = Array.isArray(block.sample) ? block.sample.slice(0, limit) : [];
  const showUnattributed = sample.some((r) => Math.abs(Number(r?.unattributed) || 0) > 0.01);
  const columns = showUnattributed
    ? cols
    : cols.filter((c) => c !== 'unattributed');
  return {
    columns,
    rows: sample,
    rowCount: block.rowCount ?? sample.length,
    showUnattributed,
    pvUnitLabel: output?.coverage?.pvUnitLabel || 'PV at coupon — not PV-10',
  };
}

const PRICED_LINE_HEADERS = {
  date: 'Month',
  well: 'Well',
  product: 'Product',
  salesVolume: 'Sales volume',
  realizedPrice: 'Realized',
  grossRevenue: 'Revenue',
  fixedLoe: 'Fixed LOE',
  variableLoe: 'Variable LOE',
  noi: 'NOI',
};

/** Lease name when the priced asset has one; otherwise drop a raw SALE property id. */
export function presentWellLabel(wellId, lease) {
  const name = String(lease || '').trim();
  if (name) return name;
  const id = String(wellId || '');
  const sale = id.match(/^SALE0*(\d+)$/i);
  if (sale) return `Property ${sale[1]}`;
  return id || '—';
}

/**
 * Coverage price-build rows. Hides the raw priced-line dump (SALE property ids
 * and engine column names) in favor of lease, product, and dollars.
 */
export function absCoveragePriceRows(output, { limit = 12 } = {}) {
  const names = {};
  for (const asset of output?.priced?.assets || []) {
    if (asset?.wellId) names[asset.wellId] = asset.lease || asset.name || '';
  }
  const sample = Array.isArray(output?.pricedLines?.sample) ? output.pricedLines.sample : [];
  const rows = [];
  for (const row of sample) {
    if (!row || row.product === 'ALL') continue;
    rows.push({
      date: row.date || '—',
      well: presentWellLabel(row.wellId, names[row.wellId]),
      product: row.product || '—',
      salesVolume: row.salesVolume,
      realizedPrice: row.realizedPrice,
      grossRevenue: row.grossRevenue,
      fixedLoe: row.fixedLoe,
      variableLoe: row.variableLoe,
      noi: row.noi,
    });
    if (rows.length >= limit) break;
  }
  return {
    headers: PRICED_LINE_HEADERS,
    columns: Object.keys(PRICED_LINE_HEADERS),
    rows,
  };
}

function _sensitivityRowFromRun(id, label, run) {
  if (!run?.solve) return null;
  const assumptions = run.priced?.assumptions || {};
  const structure = run.structure || {};
  return {
    id,
    label,
    hh: assumptions.hh ?? null,
    wti: assumptions.wti ?? null,
    targetDscr: structure.targetDscr ?? run.coverage?.targetDscr ?? null,
    illustrativeMaxPurchasePriceMM: run.solve.illustrativeMaxPurchasePriceMM ?? null,
    maxNoteSizeMM: run.solve.maxNoteSizeMM ?? null,
    bindingConstraint: run.solve.bindingConstraint ?? null,
    minDscr: run.coverage?.minDscr ?? null,
  };
}

/**
 * Sensitivity table rows. A screen run omits the tornado (`sensitivity: []`);
 * fill the table from the base / downside / stress cases already priced.
 */
export function absSensitivityRows(output, screen) {
  const direct = Array.isArray(output?.sensitivity) ? output.sensitivity.filter(Boolean) : [];
  if (direct.length) return { rows: direct, source: 'tornado' };
  const rows = [];
  const base = _sensitivityRowFromRun('base', 'Base', screen?.base || (output?.solve ? output : null));
  if (base) rows.push(base);
  const down = _sensitivityRowFromRun(
    'downside',
    downsideColumnHeader(screen),
    screen?.downside,
  );
  if (down) rows.push(down);
  const stress = _sensitivityRowFromRun('stress', 'Stress', screen?.stress);
  if (stress) rows.push(stress);
  if (rows.length) return { rows, source: 'screen' };
  return { rows: [], source: 'empty' };
}

/**
 * Build provenance fields for the AbsDesk strip from a run response.
 * Uses real API fields only: requestedDealId, packageSource, demo, runHash, ingestVersion.
 */
export function buildAbsProvenance(output, requestedDealId = null) {
  const meta = output?.meta || {};
  const summary = output?.summary || {};
  const dealId =
    meta.requestedDealId ||
    requestedDealId ||
    meta.dealId ||
    summary.dealId ||
    null;
  const packageSource = meta.packageSource || null;
  const demo = Boolean(meta.demo);
  const live = packageSource === 'live-deal-package' && !demo;
  const runHash = meta.runHash || null;
  const ingestVersion = meta.ingestVersion || summary.ingestVersion || null;
  const packageName = summary.package || meta.package || null;
  const modeLabel = demo ? 'DEMO / smoke' : live ? 'Live' : packageSource ? 'Package' : '—';

  return {
    requestedDealId: meta.requestedDealId || requestedDealId || null,
    dealId,
    packageSource,
    packageName,
    demo,
    live,
    modeLabel,
    runHash,
    ingestVersion,
  };
}

/** Human-readable package source for the strip (path-ish label when available). */
export function formatPackageSourceLabel(packageSource) {
  if (!packageSource) return '—';
  if (packageSource === 'live-deal-package') return 'live-deal-package';
  if (packageSource === 'request') return 'request body';
  return packageSource;
}

/**
 * Smoke fixtures may stamp meta.dealId as `*-smoke-fixture` while the desk
 * requests the canonical dealId (same allowlist as auth abs_routes).
 */
export const ABS_SMOKE_DEAL_ALIASES = {
  'eagle-etx-2026': Object.freeze(['eagle-etx-2026', 'eagle-smoke-fixture']),
  'juno-wtx-2026': Object.freeze(['juno-wtx-2026', 'juno-smoke-fixture']),
  'sedgwick-cks-2026': Object.freeze(['sedgwick-cks-2026', 'sedgwick-smoke-fixture']),
};

function _idsEqualOrAlias(candidate, selectedDealId) {
  if (!candidate || !selectedDealId) return false;
  if (candidate === selectedDealId) return true;
  const aliases = ABS_SMOKE_DEAL_ALIASES[selectedDealId];
  return Boolean(aliases && aliases.includes(candidate));
}

/**
 * True when an ABS run payload belongs to the desk's currently selected deal.
 * Prefer meta.requestedDealId; fall back to meta/summary dealId + smoke aliases.
 * Hard-reject when requestedDealId is present and mismatches the selection.
 */
export function absOutputMatchesDeal(output, selectedDealId) {
  if (!output || !selectedDealId) return false;
  const meta = output.meta || {};
  const summary = output.summary || {};
  const requested = meta.requestedDealId;
  if (requested) {
    return requested === selectedDealId;
  }
  return (
    _idsEqualOrAlias(meta.dealId, selectedDealId) ||
    _idsEqualOrAlias(summary.dealId, selectedDealId)
  );
}

/** Return output only when it matches the selected deal; otherwise null (discard stale). */
export function selectAbsOutputForDeal(output, selectedDealId) {
  return absOutputMatchesDeal(output, selectedDealId) ? output : null;
}

/** Canonical bid KPI label (SPEC-221a) — never "ABS-implied max bid". */
export const ABS_ILLUSTRATIVE_MAX_PURCHASE_PRICE_LABEL =
  'Illustrative max purchase price (sponsor assumptions)';

/**
 * Format a preset-sourced number's provenance next to the value: `CLASS · asOf` (+ stale).
 * Never includes screenStatus enum strings.
 */
export function formatPresetProvenance(param) {
  if (!param) return '';
  const cls = param.class || param.cls || '';
  const asOf = param.asOf || '';
  const stale = param.stale ? ' · stale' : '';
  if (!cls && !asOf) return '';
  return `${cls}${asOf ? ` · ${asOf}` : ''}${stale}`;
}

/**
 * Visible downside column header = preset subtitle only.
 * Never "FIRST-ISSUER DOWNSIDE".
 */
export function downsideColumnHeader(screenOrPreset) {
  if (!screenOrPreset) return 'internal planning downside — not an agency notch';
  if (typeof screenOrPreset === 'string') return screenOrPreset;
  return (
    screenOrPreset.downsideHeader ||
    screenOrPreset.subtitle ||
    (screenOrPreset.downside &&
      screenOrPreset.downside.meta &&
      screenOrPreset.downside.meta.preset &&
      screenOrPreset.downside.meta.preset.subtitle) ||
    'internal planning downside — not an agency notch'
  );
}

/** Forbidden copy fragments for preset / desk labels (221d). */
export const PRESET_COPY_DENYLIST = Object.freeze([
  'agency deck',
  'verified',
  'market quote',
  'FIRST-ISSUER DOWNSIDE',
]);

export function presetLabelIsClean(text) {
  const s = String(text || '').toLowerCase();
  if (!s) return true;
  if (s.includes('agency deck')) return false;
  if (/\bverified\b/.test(s)) return false;
  if (s.includes('market quote')) return false;
  if (String(text || '').includes('FIRST-ISSUER DOWNSIDE')) return false;
  return true;
}

/**
 * Side-by-side column model for BASE vs downside.
 * Always returns both columns even when downside fails / is empty.
 */
export function buildScreenColumns(screen) {
  const base = screen?.base || null;
  const downside = screen?.downside || null;
  const baseSolve = base?.solve || {};
  const downSolve = downside?.solve || {};
  const baseCov = base?.coverage || {};
  const downCov = downside?.coverage || {};
  return {
    base: {
      title: 'BASE',
      noteMM: baseSolve.maxNoteSizeMM ?? null,
      bidMM: baseSolve.illustrativeMaxPurchasePriceMM ?? baseSolve.absImpliedMaxBidMM ?? null,
      minDscr: baseCov.minDscr ?? null,
      params: (base?.meta?.preset?.params) || [],
      failed: !base,
    },
    downside: {
      title: downsideColumnHeader(screen),
      noteMM: downSolve.maxNoteSizeMM ?? null,
      bidMM: downSolve.illustrativeMaxPurchasePriceMM ?? downSolve.absImpliedMaxBidMM ?? null,
      minDscr: downCov.minDscr ?? null,
      params: (downside?.meta?.preset?.params) || [],
      failed: !downside || Boolean(downside?.error),
    },
  };
}

/** Hedge panel summary from a run (agency default). */
export function buildHedgePanel(output, view = 'agency') {
  const views = output?.views || {};
  const chosen = views[view] || views.agency || {};
  const hedges = chosen.hedges || {};
  const counts = hedges.counts || { proposed: 0, executed: 0, credited: 0 };
  const program = output?.structure?.hedgeProgram || hedges.program || null;
  return {
    view,
    program,
    counts,
    overhedgedMonths: (hedges.overhedgedByMonth || []).length,
    belowFloorMonths: (hedges.hedgeBelowFloorMonths || []).length,
    gaps: hedges.gaps || [],
  };
}

